import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Image,
  Star,
  Check,
  Loader2,
  FolderOpen,
  AlertTriangle,
  Tag as TagIcon
} from 'lucide-react';
import { creativeService, Creative } from '../../services/creativeService';
import toast from 'react-hot-toast';

interface CreativeLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (creative: Creative) => void;
  platforms?: string[];
  /** Active platform tab — used to highlight when a creative comes from a different platform */
  currentPlatform?: string;
}

const CreativeLibraryModal: React.FC<CreativeLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  platforms = [],
  currentPlatform
}) => {
  const { t } = useTranslation();
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(null);

  // Auto pre-select platform filter when only one platform is in scope (e.g. user is on Google tab)
  useEffect(() => {
    if (isOpen && platforms.length === 1) {
      setSelectedPlatform(platforms[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, platforms.join(',')]);

  useEffect(() => {
    if (isOpen) {
      loadCreatives();
    }
  }, [isOpen, search, selectedPlatform, showFavoritesOnly]);

  const loadCreatives = async () => {
    setLoading(true);
    try {
      const response = await creativeService.getCreatives({
        search: search || undefined,
        platform: selectedPlatform || undefined,
        is_favorite: showFavoritesOnly || undefined,
        limit: 50,
        sort_by: 'updated_at',
        sort_order: 'DESC'
      });

      if (response.success) {
        setCreatives(response.data);
      }
    } catch (error) {
      console.error('Error loading creatives:', error);
      toast.error(t('creatives.loadError', 'Error loading creatives'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedCreative) {
      onSelect(selectedCreative);
      // Increment usage count in background
      creativeService.incrementUsage(selectedCreative.id).catch(console.error);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('creatives.library', 'Creative Library')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('creatives.selectFromLibrary', 'Select a saved creative to use')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('creatives.searchPlaceholder', 'Search creatives...')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Platform Filter */}
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('creatives.allPlatforms', 'All Platforms')}</option>
            <option value="google">{t('common.google', 'Google')}</option>
            <option value="meta">{t('common.meta', 'Meta')}</option>
            <option value="linkedin">{t('common.linkedin', 'LinkedIn')}</option>
          </select>

          {/* Favorites Toggle */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              showFavoritesOnly
                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700'
                : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
            {t('creatives.favorites', 'Favorites')}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : creatives.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FolderOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('creatives.noCreatives', 'No creatives found')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                {t('creatives.noCreativesDesc', 'Save creatives while creating campaigns to reuse them later.')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {creatives.map((creative) => {
                const platformList = creative.platform || [];
                const hasMismatch = !!currentPlatform && platformList.length > 0 && !platformList.includes(currentPlatform);
                return (
                <div
                  key={creative.id}
                  onClick={() => setSelectedCreative(creative)}
                  className={`relative cursor-pointer rounded-xl border-2 transition-all overflow-hidden ${
                    selectedCreative?.id === creative.id
                      ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
                      : hasMismatch
                        ? 'border-yellow-300 dark:border-yellow-700 hover:border-yellow-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {/* Image Preview */}
                  <div className="aspect-video bg-gray-100 dark:bg-gray-800 relative">
                    {creative.image_url ? (
                      <img
                        src={creative.image_url}
                        alt={creative.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                      </div>
                    )}

                    {/* Selection indicator */}
                    {selectedCreative?.id === creative.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}

                    {/* Favorite indicator */}
                    {creative.is_favorite && (
                      <div className="absolute top-2 left-2">
                        <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      </div>
                    )}

                    {/* Platform mismatch warning overlay */}
                    {hasMismatch && (
                      <div
                        className="absolute bottom-2 left-2 right-2 px-2 py-1 bg-yellow-500/90 text-white text-xs font-medium rounded flex items-center gap-1"
                        title={t('creatives.platformMismatchTooltip', 'Esse creative foi salvo em outra plataforma. Os limites de caracteres serão ajustados ao carregar.')}
                      >
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {t('creatives.platformMismatch', 'Outra plataforma')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                      {creative.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                      {creative.headlines?.[0] || t('creatives.noHeadline', 'No headline')}
                    </p>
                    <div className="flex items-center justify-between mt-2 gap-1">
                      <div className="flex gap-1 flex-wrap min-w-0">
                        {platformList.map((p) => (
                          <span
                            key={p}
                            className={`px-2 py-0.5 rounded text-xs capitalize ${
                              currentPlatform === p
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {t('creatives.usedTimes', '{{count}}x', { count: creative.usage_count })}
                      </span>
                    </div>
                    {creative.tags && creative.tags.length > 0 && (
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        <TagIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        {creative.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-gray-500 dark:text-gray-400">
                            #{tag}
                          </span>
                        ))}
                        {creative.tags.length > 3 && (
                          <span className="text-xs text-gray-400">+{creative.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSelect}
            disabled={!selectedCreative}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t('creatives.useCreative', 'Use Creative')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreativeLibraryModal;
